import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { RootState } from '../Redux/store';
import { useDispatch, useSelector } from 'react-redux';
import { setAuth } from '../Redux/authSlice';
import { Navigate } from 'react-router-dom';

interface Class {
  id: number;
  name: string;
}

const AddSection: React.FC = () => {
  const [classes, setClasses] = useState<Class[]>([]);
  const [selectedClassId, setSelectedClassId] = useState<number | null>(null);
  const [sectionName, setSectionName] = useState<string>('');
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [redirectToSections, setRedirectToSections] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useSelector((state: RootState) => state.auth.value);
  const dispatch = useDispatch();

  // Fetch user data and check authentication
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = getCookie('token');
        if (!token) {
          dispatch(setAuth(false));
          return;
        }

        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        await axios.get('/users/user');
        dispatch(setAuth(true));
      } catch (e) {
        dispatch(setAuth(false));
      }
    };

    fetchUserData();
  }, [dispatch]);

  // Fetch classes data
  useEffect(() => {
    const fetchClassesData = async () => {
      try {
        const response = await axios.get('/Branches/classes/');
        setClasses(response.data);
        setLoading(false);
      } catch (err) {
        setError('Error fetching class data');
        setLoading(false);
      }
    };

    fetchClassesData();
  }, []);

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!selectedClassId || !sectionName) {
      alert('Please select a class and enter a section name.');
      return;
    }

    const newSection = {
      name: sectionName,
      class_associated: selectedClassId,
    };

    try {
      await axios.post('/Branches/sections/', newSection, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      setShowAlert(true);
      setSectionName('');
      setSelectedClassId(null);
      setRedirectToSections(true);
    } catch (error) {
      console.error('Error creating section:', error);
      alert('Failed to create section. Please try again.');
    }
  };

  // Function to get a cookie by name
  const getCookie = (name: string) => {
    const cookieName = name + '=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');
    for (let i = 0; i < cookieArray.length; i++) {
      let cookie = cookieArray[i];
      while (cookie.charAt(0) === ' ') {
        cookie = cookie.substring(1);
      }
      if (cookie.indexOf(cookieName) === 0) {
        return cookie.substring(cookieName.length, cookie.length);
      }
    }
    return '';
  };

  if (redirectToSections) {
    return <Navigate to='/section' />; // Redirect to sections page after successful submission
  }

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <div className="container">
      {auth ? (
        <div className="row justify-content-center mt-5">
          <div className="col-md-6">
            <div className="card">
              <div className="card-body">
                <h2 className="card-title text-center mb-4">Create New Section</h2>
                {showAlert && (
                  <div className="alert alert-success" role="alert">
                    Section added successfully!
                  </div>
                )}
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label htmlFor="classSelect" className="form-label">
                      Select Class
                    </label>
                    <select
                      className="form-control"
                      id="classSelect"
                      value={selectedClassId || ''}
                      onChange={(e) => setSelectedClassId(Number(e.target.value))}
                      required
                    >
                      <option value="" disabled>
                        Choose a class
                      </option>
                      {classes.map((classItem) => (
                        <option key={classItem.id} value={classItem.id}>
                          {classItem.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="sectionName" className="form-label">
                      Section Name
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="sectionName"
                      value={sectionName}
                      onChange={(e) => setSectionName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="d-grid">
                    <button type="submit" className="btn btn-primary btn-block">
                      Submit
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <h4 style={{ textAlign: 'center', marginTop: '20px' }}>You are not authenticated</h4>
        </div>
      )}
    </div>
  );
};

export default AddSection;